import React, { useEffect, useState } from 'react'
import {
  FaUserPlus, FaSpinner, FaUserShield, FaUserTie, FaUser, FaTrash, FaBan, FaCheck, FaCopy,
  FaEnvelope, FaWhatsapp, FaPaperPlane,
} from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, Modal, Toast, useToast, EmptyState } from '../components/ui'
import { useAuth } from '../context/AuthContext'
import { relativeTime, formatDate } from '../lib/format'
import { inviteMessage } from '../lib/invite'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * Who can get into the panel, and what they may do.
 *
 * The owner and managers run the team. Managers can invite staff and managers
 * and change their role or access; only the owner can create or change an
 * owner, and nobody can change their own access. That is enforced in SQL
 * (migration 18), not just by hiding buttons — someone who opened the console
 * and called the RPC directly would still be refused.
 *
 * Access is granted in two halves, because creating a login outright needs the
 * service-role key and that cannot live in a browser app:
 *
 *   1. the owner or a manager invites an e-mail address and picks a role;
 *   2. the person signs up with that address, and the panel swaps the invitation
 *      for real access on their first sign-in.
 *
 * Until both halves happen, nothing is granted. Someone who registers an account
 * without an invitation gets no access at all.
 */

const ROLES = [
  {
    value: 'owner',
    label: 'Owner',
    icon: FaUserShield,
    summary: 'Everything, including managing the team',
    can: ['Add, edit and price products', ...(SHOW_STOCK ? ['Adjust stock'] : []), 'Work orders', 'Change shop settings', 'Invite anyone and manage everyone, including owners'],
  },
  {
    value: 'manager',
    label: 'Manager',
    icon: FaUserTie,
    summary: 'The whole shop, and the team below owner',
    can: ['Add, edit and price products', ...(SHOW_STOCK ? ['Adjust stock'] : []), 'Work orders', 'Change shop settings', 'Invite staff and managers, and change their role or access'],
    cannot: ['Make someone an owner, or change an owner'],
  },
  {
    value: 'staff',
    label: 'Staff',
    icon: FaUser,
    summary: 'Day-to-day shop floor work',
    can: [...(SHOW_STOCK ? ['Adjust stock and run stocktakes'] : []), 'Work orders and update their status', 'See the dashboard and reports'],
    cannot: ['Add or edit products', 'Change prices', 'Change shop settings', 'Delete suppliers or invoices', 'Manage the team'],
  },
]

const roleMeta = (role) => ROLES.find((r) => r.value === role) ?? ROLES[2]

/**
 * The invitation as a ready-made message the owner sends themselves — from
 * their own mail app, WhatsApp, or pasted anywhere. Shown whenever the panel
 * could not e-mail it, which until an e-mail service is connected is always.
 */
const ShareInvite = ({ invitation, reason, onCopied }) => {
  const { subject, body } = inviteMessage({
    origin: window.location.origin,
    email: invitation.email,
    role: invitation.role,
    expiresAt: invitation.expires_at,
    invitedBy: invitation.invited_by_email,
  })
  const mailto = `mailto:${invitation.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(body)}`

  const copy = async () => {
    await navigator.clipboard.writeText(body)
    onCopied()
  }

  return (
    <div className="space-y-3.5">
      <Banner tone={reason === 'send-failed' ? 'warning' : 'info'}>
        {reason === 'send-failed'
          ? "The automatic e-mail didn't go through, so send this one yourself."
          : "The store manager can't send e-mail yet, so send this invitation yourself."}{' '}
        It includes their personal link.
      </Banner>

      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-hairline bg-black/[0.02] p-3 font-sans text-2xs leading-relaxed text-ink-secondary">
        {body}
      </pre>

      <div className="flex flex-wrap gap-2">
        <a href={mailto} className="btn-primary">
          <FaEnvelope className="text-[11px]" />
          Email it
        </a>
        <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="btn-ghost">
          <FaWhatsapp className="text-[12px]" />
          WhatsApp
        </a>
        <button onClick={copy} className="btn-ghost">
          <FaCopy className="text-[11px]" />
          Copy message
        </button>
      </div>
    </div>
  )
}

const TeamPage = () => {
  const { profile, isOwner, canManage, isDemo } = useAuth()

  // Owner and managers run the team. What each may do to a given row mirrors
  // the SQL in migration 18 — the database is what enforces it; this only
  // decides which controls to show.
  const canManageTeam = canManage
  const canEditMember = (member) =>
    canManageTeam && !isDemo && (isOwner || (member.role !== 'owner' && member.id !== profile?.id))
  const canHandleInvite = (invitation) => canManageTeam && (isOwner || invitation.role !== 'owner')
  const roleChoices = ROLES.filter((r) => r.value !== 'owner' || isOwner)
  const [team, setTeam] = useState([])
  const [invitations, setInvitations] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [inviting, setInviting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState({ email: '', role: 'staff', note: '' })
  const [sharing, setSharing] = useState(null) // { invitation, reason } while the send-it-yourself dialog is open
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      const [members, pending] = await Promise.all([api.getTeam(), api.getInvitations()])
      setTeam(members ?? [])
      setInvitations(pending ?? [])
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  // Recording an invitation sends nothing by itself. This gets it to the
  // person: e-mailed by the panel when that is set up, otherwise handed to the
  // owner as a message to send. Either way they are never left waiting on an
  // e-mail that was never going to arrive.
  const deliver = async (invitation) => {
    const result = await api.sendInviteEmail(invitation.email)
    if (result.sent) notify(`Invitation e-mailed to ${invitation.email}.`)
    else setSharing({ invitation, reason: result.reason })
  }

  const invite = async () => {
    setBusy(true)
    try {
      const email = draft.email.trim()
      if (!email.includes('@')) throw new Error('Enter a valid e-mail address.')
      const created = await api.inviteAdmin(email, draft.role, draft.note.trim() || null)
      setDraft({ email: '', role: 'staff', note: '' })
      setInviting(false)
      load()
      await deliver(created)
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  // An expired invitation is renewed first (invite_admin resets the 14 days),
  // so the link they receive actually works.
  const resend = async (invitation) => {
    setBusy(true)
    try {
      const expired = new Date(invitation.expires_at) < new Date()
      const current = expired
        ? await api.inviteAdmin(invitation.email, invitation.role, invitation.note ?? null)
        : invitation
      if (expired) load()
      await deliver(current)
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const changeRole = async (member, role) => {
    setBusy(true)
    try {
      await api.setAdminRole(member.id, role)
      notify(`${member.email} is now ${role}.`)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const toggleAccess = async (member) => {
    setBusy(true)
    try {
      await api.setAdminActive(member.id, !member.is_active)
      notify(member.is_active ? `${member.email} can no longer sign in.` : `${member.email} can sign in again.`)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (invitation) => {
    setBusy(true)
    try {
      await api.revokeInvitation(invitation.id)
      notify(`Invitation for ${invitation.email} withdrawn.`)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <Spinner label="Loading the team…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load the team.</strong> {state.error.message}</Banner>
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Team" subtitle="Who can sign in to the store manager, and what they can do">
        {canManageTeam && (
          <button onClick={() => setInviting(true)} className="btn-primary">
            <FaUserPlus className="text-[11px]" />
            Invite someone
          </button>
        )}
      </PageHeader>

      {!canManageTeam && (
        <Banner tone="info">
          Only the owner or a manager can invite people or change what they are allowed to do.
        </Banner>
      )}
      {canManageTeam && !isOwner && (
        <Banner tone="info">
          As a manager you can invite staff and managers, and change their role or access. Owners,
          and your own account, are managed by the owner.
        </Banner>
      )}

      {isDemo && (
        <Banner tone="warning">
          <strong>Demo mode.</strong> This is example team data — inviting and role changes need a
          live Supabase connection.
        </Banner>
      )}

      {/* ---- Current team ------------------------------------------------ */}
      <Card title="People with access" bodyClassName="">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Last seen</th>
                <th>Access</th>
                {canManageTeam && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {team.map((member) => {
                const meta = roleMeta(member.role)
                const isSelf = member.id === profile?.id
                return (
                  <tr key={member.id}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-plane text-ink-muted">
                          <meta.icon className="text-[12px]" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-ink">
                            {member.full_name || member.email}
                            {isSelf && <span className="ml-1.5 text-2xs font-normal text-ink-muted">(you)</span>}
                          </div>
                          <div className="truncate text-2xs text-ink-muted">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {canEditMember(member) ? (
                        <select
                          className="input input-sm w-auto"
                          value={member.role}
                          disabled={busy}
                          onChange={(e) => changeRole(member, e.target.value)}
                          aria-label={`Role for ${member.email}`}
                        >
                          {roleChoices.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-[13px] capitalize">{meta.label}</span>
                      )}
                    </td>
                    <td className="text-2xs text-ink-secondary">
                      {member.last_seen_at ? relativeTime(member.last_seen_at) : 'Never signed in'}
                    </td>
                    <td>
                      {member.is_active ? (
                        <span className="text-2xs font-medium text-status-good-text">Active</span>
                      ) : (
                        <span className="text-2xs font-medium text-status-critical">Suspended</span>
                      )}
                    </td>
                    {canManageTeam && (
                      <td className="text-right">
                        {/* No self-suspend button: the SQL refuses it, and an
                            error message is a worse way to learn that than the
                            button not existing. */}
                        {!isSelf && canEditMember(member) && (
                          <button
                            onClick={() => toggleAccess(member)}
                            disabled={busy}
                            className="btn-subtle btn-sm"
                            title={member.is_active ? 'Suspend access' : 'Restore access'}
                          >
                            {member.is_active ? <FaBan /> : <FaCheck />}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Pending invitations ----------------------------------------- */}
      {canManageTeam && (
        <Card
          title="Pending invitations"
          subtitle="They get access once they sign up with the invited e-mail address"
          bodyClassName=""
        >
          {invitations.length === 0 ? (
            <EmptyState icon={FaUserPlus} title="No invitations outstanding">
              Invite someone and they will appear here until they have signed up.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Invited</th>
                    <th>Expires</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => {
                    const expired = new Date(invitation.expires_at) < new Date()
                    return (
                      <tr key={invitation.id}>
                        <td className="font-medium">{invitation.email}</td>
                        <td className="capitalize text-2xs">{invitation.role}</td>
                        <td className="text-2xs text-ink-secondary">
                          {relativeTime(invitation.created_at)}
                          {invitation.invited_by_email && (
                            <div className="text-ink-muted">by {invitation.invited_by_email}</div>
                          )}
                        </td>
                        <td className="text-2xs">
                          {expired ? (
                            <span className="font-medium text-status-critical">Expired</span>
                          ) : (
                            <span className="text-ink-secondary">{formatDate(invitation.expires_at)}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {canHandleInvite(invitation) && (
                            <>
                              <button
                                onClick={() => resend(invitation)}
                                disabled={busy}
                                className="btn-subtle btn-sm"
                                title={expired ? 'Renew and send the invitation' : 'Send the invitation again'}
                                aria-label={`Send the invitation to ${invitation.email}`}
                              >
                                <FaPaperPlane />
                              </button>
                              <button
                                onClick={() => revoke(invitation)}
                                disabled={busy}
                                className="btn-subtle btn-sm text-brand-red"
                                title="Withdraw this invitation"
                                aria-label={`Withdraw the invitation for ${invitation.email}`}
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ---- What each role can do --------------------------------------- */}
      <Card title="What each role can do">
        <div className="grid gap-4 sm:grid-cols-3">
          {ROLES.map((role) => (
            <div key={role.value} className="rounded-lg border border-hairline p-3.5">
              <div className="flex items-center gap-2">
                <role.icon className="text-[13px] text-ink-muted" aria-hidden="true" />
                <h3 className="text-[13px] font-semibold text-ink">{role.label}</h3>
              </div>
              <p className="mt-1 text-2xs text-ink-muted">{role.summary}</p>

              <ul className="mt-2.5 space-y-1">
                {role.can.map((item) => (
                  <li key={item} className="flex items-start gap-1.5 text-2xs text-ink-secondary">
                    <FaCheck className="mt-0.5 shrink-0 text-[8px] text-status-good" aria-hidden="true" />
                    {item}
                  </li>
                ))}
                {(role.cannot ?? []).map((item) => (
                  <li key={item} className="flex items-start gap-1.5 text-2xs text-ink-muted">
                    <FaBan className="mt-0.5 shrink-0 text-[8px] text-status-critical" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      {/* ---- Invite dialog ----------------------------------------------- */}
      <Modal
        open={inviting}
        onClose={() => setInviting(false)}
        title="Invite someone to the store manager"
        footer={
          <>
            <button onClick={() => setInviting(false)} className="btn-ghost">Cancel</button>
            <button onClick={invite} disabled={busy} className="btn-primary">
              {busy && <FaSpinner className="animate-spin" />}
              Create invitation
            </button>
          </>
        }
      >
        <div className="space-y-3.5">
          <div>
            <label className="label" htmlFor="invite-email">Their e-mail address</label>
            <input
              id="invite-email"
              type="email"
              className="input"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              placeholder="name@coquis.co.za"
              autoFocus
            />
            <p className="field-hint">
              They must sign up with this exact address. Anything else gets no access.
            </p>
          </div>

          <div>
            <span className="label">What can they do?</span>
            <div className="space-y-2">
              {roleChoices.map((role) => (
                <label
                  key={role.value}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors ${
                    draft.role === role.value
                      ? 'border-brand-navy/40 bg-brand-navy/[0.04]'
                      : 'border-hairline hover:border-baseline'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={draft.role === role.value}
                    onChange={() => setDraft({ ...draft, role: role.value })}
                    className="mt-0.5 h-4 w-4 border-hairline text-brand-navy focus:ring-brand-navy/30"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-ink">{role.label}</span>
                    <span className="block text-2xs text-ink-muted">{role.summary}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="invite-note">Note (optional)</label>
            <input
              id="invite-note"
              className="input"
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              placeholder="e.g. Saturday shop floor"
            />
          </div>

          <Banner tone="info">
            They get a personal link to create their account, and are let straight in with the
            role you picked. If the store manager can't e-mail it, you get a ready-made message
            to send from your own e-mail or WhatsApp.
          </Banner>
        </div>
      </Modal>

      {/* ---- Send it yourself -------------------------------------------- */}
      <Modal
        open={Boolean(sharing)}
        onClose={() => setSharing(null)}
        title={sharing ? `Send the invitation to ${sharing.invitation.email}` : ''}
        footer={<button onClick={() => setSharing(null)} className="btn-ghost">Done</button>}
      >
        {sharing && (
          <ShareInvite
            invitation={sharing.invitation}
            reason={sharing.reason}
            onCopied={() => notify('Invitation copied — paste it into any message.')}
          />
        )}
      </Modal>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

export default TeamPage
